/**
 * Text Input Block Renderer
 *
 * The `ui_text_input` card: a titled question with a textarea and a submit
 * button (create-routine's "When should I alert you?" ask). The LLM pre-fills
 * `value` when the user's message already contained the answer; the submit
 * button stays disabled while the textarea is blank. After submit (or when
 * rehydrated from history via `submittedValue`) the textarea renders disabled
 * with the final answer and the button is hidden.
 */

import React from "react";
import { TextArea } from "@strivelabs/ui-core";
import { Button } from "@strivelabs/ui-core/v2";
import type { UIBlock, UIActionEvent } from "@/types/ui-blocks";

export interface TextInputBlockRendererProps {
  block: UIBlock;
  onUIAction: (event: UIActionEvent) => void;
  /** When true, the component is disabled (historical render). */
  readOnly?: boolean;
}

export function TextInputBlockRenderer({
  block,
  onUIAction,
  readOnly = false,
}: TextInputBlockRendererProps) {
  const content = block.type === "text_input" ? block.content : null;
  const submittedValue = content?.submittedValue;

  const [value, setValue] = React.useState<string>(submittedValue ?? content?.value ?? "");
  const [submitted, setSubmitted] = React.useState<boolean>(Boolean(submittedValue));

  // Re-seed when a NEW card streams in (e.g. the agent re-presents the ask
  // pre-filled after an invalid answer).
  React.useEffect(() => {
    setValue(submittedValue ?? content?.value ?? "");
    setSubmitted(Boolean(submittedValue));
  }, [content?.title, content?.value, submittedValue]);

  if (block.type !== "text_input" || !content) return null;

  const disabled = readOnly || submitted;

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (disabled || !trimmed) return;
    setSubmitted(true);
    onUIAction({
      type: "ui_action",
      component: "text_input",
      label: content.title,
      state: { value: trimmed },
    });
  };

  return (
    <div className="my-2 w-full rounded-lg border border-custom-border bg-[#fbfbfb] p-5">
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-medium text-gray-900">{content.title}</h3>
        {content.description ? (
          <p className="text-sm text-gray-500">{content.description}</p>
        ) : null}
      </div>

      <div className="py-4">
        <TextArea
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder={content.placeholder ?? undefined}
          disabled={disabled}
          className="min-h-[64px] bg-white text-sm disabled:bg-gray-100 disabled:text-gray-600"
          onKeyDown={(event) => {
            if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              handleSubmit();
            }
          }}
        />
      </div>

      {!disabled ? (
        <div className="flex justify-end">
          <Button size="sm" disabled={!value.trim()} onClick={handleSubmit}>
            {content.submit_label ?? "Continue"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
